import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Phone, Shield, Flame, Heart, AlertCircle, Loader2, FireExtinguisher } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const RandomCall = ({ studentInfo }) => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  const studentFullName = studentInfo ? `${studentInfo.first_name} ${studentInfo.last_name}` : 'Répartiteur';

  useEffect(() => {
    fetchAllScenarios();
  }, []);

  const fetchAllScenarios = async () => {
    try {
      const response = await axios.get(`${API}/scenarios`);
      setScenarios(response.data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      toast.error('Erreur lors du chargement des scénarios');
    }
  };

  const serviceTypes = [
    {
      id: 'police',
      name: 'Police',
      icon: Shield,
      gradient: 'linear-gradient(135deg, #002768, #2E6CF5)',
      description: 'Agressions, vols, menaces, etc.'
    },
    {
      id: 'incendie',
      name: 'Incendie',
      icon: FireExtinguisher,
      gradient: 'linear-gradient(135deg, #DC2626, #B91C1C)',
      description: 'Incendies, fumée, odeurs suspectes, etc.'
    },
    {
      id: 'ambulance',
      name: 'Ambulance',
      icon: Heart,
      gradient: 'linear-gradient(135deg, #24B219, #82F486)',
      description: 'Blessures, malaises, urgences médicales, etc.'
    }
  ];

  const personalityTypes = [
    { id: 'panicked', name: 'Paniqué' },
    { id: 'calm', name: 'Calme' },
    { id: 'annoyed', name: 'Irrité' },
    { id: 'quiet', name: 'Peu bavard' },
    { id: 'confused', name: 'Confus' },
    { id: 'elderly', name: 'Personne âgée' },
    { id: 'child', name: 'Enfant/Adolescent' },
    { id: 'hysterical', name: 'Hystérique' },
    { id: 'suspicious', name: 'Méfiant' },
    { id: 'language_barrier', name: 'Barrière linguistique' }
  ];

  const startRandomCall = async (service) => {
    setSelectedService(service);
    setIsStarting(true);

    try {
      const serviceScenarios = scenarios.filter(s => s.category === service.id);
      
      if (serviceScenarios.length === 0) {
        toast.error(`Aucun scénario disponible pour ${service.name}`);
        setIsStarting(false);
        return;
      }

      const englishScenarios = serviceScenarios.filter(s => s.language === 'english');
      const frenchScenarios = serviceScenarios.filter(s => (s.language || 'french') === 'french');
      
      let randomScenario;
      const languageRoll = Math.random();
      
      if (languageRoll < 0.35 && englishScenarios.length > 0) {
        randomScenario = englishScenarios[Math.floor(Math.random() * englishScenarios.length)];
      } else if (frenchScenarios.length > 0) {
        randomScenario = frenchScenarios[Math.floor(Math.random() * frenchScenarios.length)];
      } else {
        randomScenario = serviceScenarios[Math.floor(Math.random() * serviceScenarios.length)];
      }
      
      const randomPersonality = personalityTypes[Math.floor(Math.random() * personalityTypes.length)];

      sessionStorage.setItem('random_call_scenario', JSON.stringify(randomScenario));
      sessionStorage.setItem('random_call_personality', randomPersonality.id);
      sessionStorage.setItem('is_random_call', 'true');
      sessionStorage.setItem('selected_training_language', randomScenario.language || 'french');

      navigate(`/training/${randomScenario.id}`);
      
    } catch (error) {
      console.error('Error starting random call:', error);
      toast.error("Erreur lors du démarrage de l'appel");
      setIsStarting(false);
    }
  };

  return (
    <div data-testid="random-call-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#002768' }}>Appels 911 Aléatoires</h1>
        <p className="text-lg text-gray-600">
          Bienvenue <span className="font-semibold" style={{ color: '#002768' }}>{studentFullName}</span> — choisissez un service pour commencer
        </p>
      </div>

      <Card className="glass border-0 mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Phone className="w-5 h-5" style={{ color: '#2E6CF5' }} />
            <span>Choisissez le Type de Service</span>
          </CardTitle>
          <CardDescription>
            Sélectionnez le service d'urgence pour lequel vous voulez vous entraîner
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {serviceTypes.map((service) => {
          const Icon = service.icon;
          const isLoading = isStarting && selectedService?.id === service.id;
          return (
            <button
              key={service.id}
              onClick={() => startRandomCall(service)}
              disabled={isStarting}
              className="group relative overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-2xl disabled:opacity-70 disabled:cursor-not-allowed"
              data-testid={`service-${service.id}`}
            >
              <div className="absolute inset-0 transition-all" style={{ background: service.gradient }}></div>
              <div className="relative p-8 text-white">
                {isLoading ? (
                  <Loader2 className="w-16 h-16 mb-4 mx-auto animate-spin" />
                ) : (
                  <Icon className="w-16 h-16 mb-4 mx-auto" />
                )}
                <h3 className="text-2xl font-bold mb-2">{service.name}</h3>
                <p className="text-sm opacity-90">{service.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      <Card className="glass border-0 mt-8">
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#2E6CF5' }} />
            <div className="text-sm text-gray-700">
              <p className="font-semibold mb-2">Mode Aléatoire :</p>
              <ul className="space-y-1 text-gray-600">
                <li>Le scénario sera choisi aléatoirement dans la catégorie sélectionnée</li>
                <li>La personnalité du citoyen sera également aléatoire</li>
                <li>Vous ne connaîtrez pas la situation avant de répondre à l'appel</li>
                <li>Cela simule les conditions réelles d'un centre 911</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RandomCall;
